import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import lottie from 'lottie-web/build/player/lottie_light'
import scratchingAnimation from '~/assets/animations/scratching.json'

import {
  CARD_HEIGHT,
  GRADIENT_COLORS,
  NOTCH_RADIUS,
  Styles as S,
  SEPARATOR_Y,
  makeCardGradient,
} from './styles'

const CANVAS_WIDTH = 280
const CANVAS_HEIGHT = SEPARATOR_Y
const BRUSH_SIZE = 90
const CORNER_RADIUS = 28
const SCRATCH_DURATION = 2000
const FINISH_DISTANCE = 700

type TPoint = { x: number; y: number }

const SCRATCH_PATH: TPoint[] = [
  { x: 155, y: 20 },
  { x: 100, y: 90 },
  { x: 210, y: 160 },
  { x: 100, y: 230 },
  { x: 155, y: 265 },
]

const LOCALE_MAP: Record<string, string> = {
  en: 'en-US',
  pl: 'pl-PL',
  tr: 'tr-TR',
}

const getPromoCode = (email: string, lng: string) => {
  const username = (email.split('@')[0] || 'user').slice(0, 7)
  const now = new Date()
  const locale = LOCALE_MAP[lng] ?? 'en-US'
  const month = now.toLocaleString(locale, { month: 'long' }).toLowerCase()
  return `${username}_${month}${String(now.getFullYear()).slice(-2)}`
}

const getPointOnPath = (progress: number): TPoint => {
  const clamped = Math.min(Math.max(progress, 0), 1)
  const totalSegments = SCRATCH_PATH.length - 1
  const rawSegment = clamped * totalSegments
  const segmentIndex = Math.min(Math.floor(rawSegment), totalSegments - 1)
  const segmentProgress = rawSegment - segmentIndex
  const p1 = SCRATCH_PATH[segmentIndex]
  const p2 = SCRATCH_PATH[segmentIndex + 1]
  return {
    x: p1.x + (p2.x - p1.x) * segmentProgress,
    y: p1.y + (p2.y - p1.y) * segmentProgress,
  }
}

interface IScratchCardProps {
  discount: number
  email: string
  isDark?: boolean
  gradientColors?: { start: string; mid: string; end: string }
  cardBackground?: string
  bottomOverlayColor?: string
  canvasOverlayOpacity?: number
  hintAnimationFilter?: string
  promoCheckmark?: ReactNode
  onStart: () => void
  onComplete: () => void
}

const drawTopSection = (
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  colors: { start: string; mid: string; end: string } = GRADIENT_COLORS,
  overlayOpacity: number = 0.3,
) => {
  ctx.save()
  ctx.beginPath()
  ctx.moveTo(CORNER_RADIUS, 0)
  ctx.lineTo(w - CORNER_RADIUS, 0)
  ctx.arcTo(w, 0, w, CORNER_RADIUS, CORNER_RADIUS)
  ctx.lineTo(w, h)
  ctx.lineTo(0, h)
  ctx.lineTo(0, CORNER_RADIUS)
  ctx.arcTo(0, 0, CORNER_RADIUS, 0, CORNER_RADIUS)
  ctx.closePath()
  ctx.clip()

  const gradient = ctx.createLinearGradient(w, 0, 0, CARD_HEIGHT)
  gradient.addColorStop(0, colors.start)
  gradient.addColorStop(0.5, colors.mid)
  gradient.addColorStop(1, colors.end)
  ctx.fillStyle = gradient
  ctx.fill()

  ctx.fillStyle = `rgba(0,0,0,${overlayOpacity})`
  ctx.fill()

  ctx.beginPath()
  ctx.moveTo(CORNER_RADIUS + 6, 6)
  ctx.lineTo(w - CORNER_RADIUS - 6, 6)
  ctx.arcTo(w - 6, 6, w - 6, CORNER_RADIUS + 6, CORNER_RADIUS)
  ctx.moveTo(6, CORNER_RADIUS + 6)
  ctx.arcTo(6, 6, CORNER_RADIUS + 6, 6, CORNER_RADIUS)
  ctx.strokeStyle = 'rgba(255,255,255,0.1)'
  ctx.lineWidth = 1
  ctx.stroke()

  ctx.restore()

  ctx.globalCompositeOperation = 'destination-out'
  ctx.fillStyle = 'rgba(0,0,0,1)'
  ctx.beginPath()
  ctx.arc(0, h, NOTCH_RADIUS, 0, Math.PI * 2)
  ctx.fill()
  ctx.beginPath()
  ctx.arc(w, h, NOTCH_RADIUS, 0, Math.PI * 2)
  ctx.fill()
  ctx.globalCompositeOperation = 'source-over'
}

export const ScratchCard: FC<IScratchCardProps> = ({
  discount,
  email,
  isDark = false,
  gradientColors = GRADIENT_COLORS,
  cardBackground,
  bottomOverlayColor,
  canvasOverlayOpacity = 0.3,
  hintAnimationFilter,
  promoCheckmark,
  onStart,
  onComplete,
}) => {
  const { i18n } = useTranslation()
  const promoCode = getPromoCode(email, i18n.language)
  const [loaded, setLoaded] = useState(false)
  const [isAutoScratch, setIsAutoScratch] = useState(false)
  const [isScratchStarted, setIsScratchStarted] = useState(false)
  const [isFinished, setIsFinished] = useState(false)
  const [isContentVisible, setIsContentVisible] = useState(false)
  const [isPromoVisible, setIsPromoVisible] = useState(false)

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null)
  const isFinishedRef = useRef(false)
  const isAnimatingRef = useRef(false)
  const isDrawingRef = useRef(false)
  const hasManualScratchRef = useRef(false)
  const lastPointRef = useRef<TPoint | null>(null)
  const scratchDistanceRef = useRef(0)
  const animationRef = useRef<HTMLDivElement>(null)
  const handRef = useRef<HTMLDivElement>(null)
  const autoStartTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const rafRef = useRef<number>()
  const promoTimerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (animationRef.current && loaded) {
      lottie.loadAnimation({
        container: animationRef.current,
        animationData: scratchingAnimation,
        name: 'scratch',
        autoplay: true,
        loop: true,
      })
    }

    return () => lottie.destroy('scratch')
  }, [loaded])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return () => undefined

    const preventTouchScroll = (e: TouchEvent) => {
      if (isDrawingRef.current) e.preventDefault()
    }

    canvas.addEventListener('touchstart', preventTouchScroll, {
      passive: false,
    })
    canvas.addEventListener('touchmove', preventTouchScroll, { passive: false })

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return () => {
        canvas.removeEventListener('touchstart', preventTouchScroll)
        canvas.removeEventListener('touchmove', preventTouchScroll)
      }
    }

    ctxRef.current = ctx
    drawTopSection(
      ctx,
      CANVAS_WIDTH,
      CANVAS_HEIGHT,
      gradientColors,
      canvasOverlayOpacity,
    )
    setLoaded(true)

    return () => {
      canvas.removeEventListener('touchstart', preventTouchScroll)
      canvas.removeEventListener('touchmove', preventTouchScroll)
    }
  }, [])

  useEffect(() => {
    return () => {
      clearTimeout(promoTimerRef.current)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [])

  const getMouse = (
    e: MouseEvent | TouchEvent,
    canvas: HTMLCanvasElement,
  ): TPoint => {
    const { top, left } = canvas.getBoundingClientRect()
    const scrollTop = window.scrollY
    const scrollLeft = window.scrollX

    if ('pageX' in e && e.pageX) {
      return { x: e.pageX - left - scrollLeft, y: e.pageY - top - scrollTop }
    }

    if ('touches' in e && e.touches.length) {
      return {
        x: e.touches[0].clientX - left - scrollLeft,
        y: e.touches[0].clientY - top - scrollTop,
      }
    }

    return { x: 0, y: 0 }
  }

  const distanceBetween = (p1: TPoint | null, p2: TPoint | null) => {
    if (!p1 || !p2) return 0
    return Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2)
  }

  const eraseStroke = (
    ctx: CanvasRenderingContext2D,
    from: TPoint,
    to: TPoint,
  ) => {
    ctx.globalCompositeOperation = 'destination-out'
    ctx.strokeStyle = 'rgba(0,0,0,1)'
    ctx.lineWidth = BRUSH_SIZE
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.beginPath()
    ctx.moveTo(from.x, from.y)
    ctx.lineTo(to.x, to.y)
    ctx.stroke()
  }

  const handleComplete = useCallback(() => {
    if (isFinishedRef.current) return
    isFinishedRef.current = true

    if (canvasRef.current) {
      canvasRef.current.style.transition = 'opacity 0.5s ease'
      canvasRef.current.style.opacity = '0'
    }

    setIsFinished(true)
    setIsContentVisible(true)
    promoTimerRef.current = setTimeout(() => setIsPromoVisible(true), 500)
    onComplete()
  }, [onComplete])

  const startScratchAnimation = useCallback(() => {
    if (isAnimatingRef.current || isFinishedRef.current) return
    isAnimatingRef.current = true
    setIsAutoScratch(true)
    setIsContentVisible(true)
    onStart()

    const startTime = performance.now()

    const step = (lastPt: TPoint) => {
      const elapsed = performance.now() - startTime
      const progress = Math.min(elapsed / SCRATCH_DURATION, 1)
      const currentPt = getPointOnPath(progress)

      const ctx = ctxRef.current
      if (ctx) eraseStroke(ctx, lastPt, currentPt)

      if (handRef.current) {
        handRef.current.style.left = `${currentPt.x}px`
        handRef.current.style.top = `${currentPt.y}px`
      }

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(() => step(currentPt))
      } else {
        handleComplete()
      }
    }

    rafRef.current = requestAnimationFrame(() => step({ ...SCRATCH_PATH[0] }))
  }, [handleComplete, onStart])

  useEffect(() => {
    if (!loaded) return
    autoStartTimerRef.current = setTimeout(startScratchAnimation, 3000)
    return () => clearTimeout(autoStartTimerRef.current)
  }, [loaded, startScratchAnimation])

  const handlePointerDown = useCallback(
    (e: any) => {
      if (isFinishedRef.current) return
      // Cancel auto-start timer and any running auto-animation
      clearTimeout(autoStartTimerRef.current)
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = undefined
      }
      isAnimatingRef.current = false
      hasManualScratchRef.current = true

      isDrawingRef.current = true
      lastPointRef.current = getMouse(e, canvasRef.current as HTMLCanvasElement)
      setIsScratchStarted(true)
      setIsContentVisible(true)
      onStart()
    },
    [onStart],
  )

  const handlePointerMove = useCallback(
    (e: any) => {
      if (!isDrawingRef.current) return

      const canvas = canvasRef.current
      const ctx = ctxRef.current
      if (!canvas || !ctx) return

      const currentPoint = getMouse(e, canvas)
      const dist = distanceBetween(lastPointRef.current, currentPoint)

      eraseStroke(ctx, lastPointRef.current ?? currentPoint, currentPoint)

      lastPointRef.current = currentPoint
      scratchDistanceRef.current += dist

      if (scratchDistanceRef.current >= FINISH_DISTANCE) {
        handleComplete()
      }
    },
    [handleComplete],
  )

  const handlePointerUp = () => {
    isDrawingRef.current = false
    if (!isFinishedRef.current && !hasManualScratchRef.current) {
      clearTimeout(autoStartTimerRef.current)
      autoStartTimerRef.current = setTimeout(startScratchAnimation, 3000)
    }
  }

  return (
    <S.Container
      onMouseDown={handlePointerDown}
      onTouchStart={handlePointerDown}
      onMouseMove={handlePointerMove}
      onTouchMove={handlePointerMove}
      onMouseUp={handlePointerUp}
      onTouchEnd={handlePointerUp}
    >
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', top: 0, zIndex: 2 }}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
      />

      {!isScratchStarted && !isAutoScratch && !isFinished && (
        <S.Animation ref={animationRef} $filter={hintAnimationFilter} />
      )}

      {isAutoScratch && !isFinished && (
        <S.HandCursor ref={handRef}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="44"
            height="44"
            viewBox="0 0 24 24"
            fill="white"
          >
            <path d="M23 5.5V20c0 2.2-1.8 4-4 4h-7.3c-1.1 0-2-.5-2.7-1.3L1 14c-.2-.2-.3-.6-.3-.9v-.3c0-1.1.9-2 2-2h.3c.5 0 .9.2 1.3.5l2.7 2.7v-9c0-1.1.9-2 2-2s2 .9 2 2v4c0-1.1.9-2 2-2s2 .9 2 2v1c0-1.1.9-2 2-2s2 .9 2 2v1c0-1.1.9-2 2-2s2 .9 2 2z" />
          </svg>
        </S.HandCursor>
      )}

      <S.RevealedCard
        $gradient={cardBackground ?? makeCardGradient(gradientColors)}
        style={{ visibility: loaded ? 'visible' : 'hidden' }}
      >
        <S.CardTop />
        <S.Separator $isDark={isDark}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="274"
            height="4"
            viewBox="0 0 274 4"
            fill="none"
            style={{ display: 'block', margin: '0 auto' }}
          >
            <path
              d="M1.91797 1.91602L272.082 1.91599"
              stroke="currentColor"
              strokeWidth="3.8324"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="16 11.5"
              strokeDashoffset="10.418"
            />
          </svg>
        </S.Separator>
        <S.CardBottom $finished={isFinished} $overlayColor={bottomOverlayColor}>
          <S.CardBottomContent $visible={isPromoVisible}>
            <S.PromoBox>
              <S.PromoLabel>
                <Trans i18nKey="onboarding.scratchAnatomy.promoCode" />
              </S.PromoLabel>
              <S.PromoCode>
                {promoCheckmark ?? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="13"
                    viewBox="0 0 18 13"
                    fill="none"
                  >
                    <path
                      d="M1.21875 6.09643L6.09594 10.9736L15.8503 1.21924"
                      stroke="#7B00DA"
                      strokeWidth="2.4386"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
                {promoCode}
              </S.PromoCode>
            </S.PromoBox>
          </S.CardBottomContent>
        </S.CardBottom>

        <S.Text $visible={isContentVisible}>
          <S.TopBox
            $bordered={isPromoVisible}
            $gradient={cardBackground ?? makeCardGradient(gradientColors)}
          >
            <S.DiscountRow>
              <S.Discount>{discount}</S.Discount>
              <S.DiscountPercent>%</S.DiscountPercent>
            </S.DiscountRow>
            <S.DiscountWord>
              <Trans i18nKey="onboarding.scratchAnatomy.discountWord" />
            </S.DiscountWord>
            <S.PlanName>
              <Trans i18nKey="onboarding.scratchAnatomy.planName" />
            </S.PlanName>
          </S.TopBox>
        </S.Text>
      </S.RevealedCard>
    </S.Container>
  )
}
